import { takeEvery, call, all, select, put, fork, take } from 'redux-saga/effects';
import { store } from '../../store';
import { requestApi, isNetworkError }  from '../../request-api.js';
import { ReduxUtils } from '@dw/pwa-helpers/redux-utils';
import * as actions from './actions';
import * as selectors from './selectors';

import * as app from '../app';
import * as auth from '../auth';
import * as router from '../router';
import firestoreRedux from '@dreamworld/firestore-redux';

import get from 'lodash-es/get';
import isEqual from 'lodash-es/isEqual';
import isEmpty from 'lodash-es/isEmpty';
import moment from 'moment/src/moment';

function* loadData() {
  yield all([
    fork(loadDeviceDetails),
    fork(autoRefreshFcmToken),
    takeEvery(auth.actions.UPDATE_AUTH, autoRefreshFcmToken),
    takeEvery(auth.actions.UPDATE_AUTH, loadDeviceDetails)
  ]);
}

function* loadDeviceDetails(action) {
  const state = yield select();
  const deviceId = selectors.getId(state);
  if(deviceId) {
    try {
      const deviceUIQuery = firestoreRedux.getDocById(`device-ui`, `du_${deviceId}`, { requesterId: `devices` });
      yield deviceUIQuery.result;
    } catch (error) {
      console.error(error);
    }
  }
  
  const userId = auth.selectors.currentUserId(state);
  if(!userId) {
    return;
  }
  
  try {
    const deviceId = selectors.getId(state);
    firestoreRedux.getDocById(`users/${userId}/devices`, `ud_${deviceId}`, { requesterId: `devices` });
  } catch (error) {
    console.error("device > loadDeviceDetails: failed due to this: ", error);
  }
}

let unsubscribeAutoRefreshFcmTokenRequest;
function* autoRefreshFcmToken() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  if(!userId) {
    return;
  }

  const deviceId = selectors.getId(state);
  const config = app.selectors.config(state)
  const fcmTokenRefreshTime = get(config, 'fcmTokenRefreshTime') || 30;
  unsubscribeAutoRefreshFcmTokenRequest && unsubscribeAutoRefreshFcmTokenRequest();
  unsubscribeAutoRefreshFcmTokenRequest = ReduxUtils.subscribe(store, `firestore.docs.devices.${`ud_${deviceId}`}.fcm.refreshedAt`, (refreshedAt) => {
    if(refreshedAt && moment().isSame(moment(refreshedAt).add(fcmTokenRefreshTime, 'd'), 'day')) {
      store.dispatch(actions.autoRefreshFcmToken());
    }
  });
}

function* onAutoTokenRefresh({token}) {
  if(!token) {
    try {
      token = yield window.cordova.plugins.firebase.messaging.getToken();
    } catch (error) {}
  }

  if(token) {
    yield call(onUpdateFcmToken, token);
  }
}

function* onUpdateFcmToken({token}) {
  if(!token) {
    return;
  }

  try {
    yield call(requestApi, `/user/users/me/refresh-fcm-token`, {method: 'POST', body: {token}});
    yield put(actions.updateFcmTokenDone(token));
    localStorage && localStorage.setItem && localStorage.setItem('device-token', token);
  } catch (error) {
    yield put(actions.updateFcmTokenFailed(token, error && error.code));
    if(isNetworkError(error)) {
      return;
    }
    console.error("device > onUpdateFcmToken: failed due to this: ", error);
  }
}

function* onDeniedToken() {
  try {
    yield call(requestApi, `/user/users/me/denied-fcm-token`, {method: 'POST'});
    yield put(actions.deniedFcmTokenDone());
  } catch (error) {
    yield put(actions.deniedFcmTokenFailed(error && error.code));
    if(isNetworkError(error)) {
      return;
    }
    console.error("device > onDeniedToken: failed due to this: ", error);
  }
}

/**
 * `utm_*` params captured on `device-ui`. Any other `utm_`-prefixed param found in the URL is
 * ignored: a mangled link (e.g. the campaign url pasted into the middle of its own `utm_source`)
 * otherwise ends up stored as a garbage Firestore field name.
 */
const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'];

/**
 * Max length of a stored UTM value.
 */
const UTM_VALUE_MAX_LENGTH = 255;

/**
 * Builds `utmDetails` from the current page params.
 *
 * A param repeated in the url (e.g. `?utm_medium=search&utm_medium=search`) is parsed into an
 * Array by `URI.query(true)`, so only the first value is kept. The stored value must always be a
 * String: `device-ui` is read by the server and it fails to deserialize anything else.
 *
 * @param {Object} pageParams Current page query params.
 * @returns {Object} UTM details, keyed without the `utm_` prefix.
 */
const normalizeUtmDetails = (pageParams) => {
  const utmDetails = {};
  for(const key of UTM_KEYS) {
    const rawValue = pageParams[`utm_${key}`];
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    if(typeof value !== 'string') {
      continue;
    }
    const utmValue = value.trim().slice(0, UTM_VALUE_MAX_LENGTH);
    if(utmValue) {
      utmDetails[key] = utmValue;
    }
  }
  return utmDetails;
}

function* routeChangeHandler() {
  const state = yield select();
  const pageParams = router.selectors.pageParams(state);
  if(!pageParams) {
    return;
  }
  const utmDetails = normalizeUtmDetails(pageParams);
  if(Object.keys(utmDetails).length) {
    yield call(saveDeviceUtmDetails, utmDetails);
  }
}

function* saveDeviceUtmDetails(utmDetails) {
  const state = yield select();
  const deviceId = selectors.getId(state);
  try {
    const deviceReq = firestoreRedux.getDocById(`device-ui`, `du_${deviceId}`);
    const currentDoc = yield deviceReq.result || {};
    const hasInitial = !isEmpty(currentDoc && currentDoc.initialUtmDetails);
    const doc = {
      id: `du_${deviceId}`,
      deviceId,
      utmDetails,
      initialUtmDetails: hasInitial ? currentDoc.initialUtmDetails : utmDetails
    };
    if(isEqual(currentDoc, doc)) {
      return;
    }
    firestoreRedux.save('device-ui', doc, { remoteWrite: true, localWrite: true});
  } catch (error) {
    console.error("device > saga > saveDeviceUtmDetails: failed due to this: ", error);
  }
}

/**
 * Watches router change.
 */
function* watchRouter() {
  //If page is already opened, check once.
  yield call(routeChangeHandler);
  yield takeEvery(router.actions.UPDATE_ROUTER, routeChangeHandler);
}

function* init() {
  yield fork(loadData),
  yield fork(watchRouter)
}

/**
 * Init Saga.
 */
function* deviceSaga() {
  yield all([
    takeEvery(actions.INIT, init),
    takeEvery(actions.UPDATE_FCM_TOKEN, onUpdateFcmToken),
    takeEvery(actions.DENIED_FCM_TOKEN, onDeniedToken),
    takeEvery(actions.AUTO_REFRESH_FCM_TOKEN, onAutoTokenRefresh)
  ]);
}

export default deviceSaga;